import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useViewport from '../../hooks/useViewport';
import { emitGameEvent, GAME_EVENTS } from '../../utils/gameEventBus';
import { presetFor } from '../../utils/roomPurposeLayout';
import {
  availableIds, loadLayout, saveLayout, defaultLayout, sameLayout, unplacedPanels, panelFor,
  addPanel, removePanel, movePanel, toggleCollapsed,
} from '../../utils/roomPanels';
import { RoomShellStyle, RsIcon, accentVars, cleanAccent, cleanMode } from './roomShellStyle';
import RoomShellTopBar from './RoomShellTopBar';
import RoomRail from './RoomRail';
import RoomPeople from './RoomPeople';
import ToolShelf from './ToolShelf';
import RoomShellSheet from './RoomShellSheet';
import RoomShellPhoneBar from './RoomShellPhoneBar';

// 🧱🗂️ THE ROOM FRAME (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.2-§5.5a; the
// approved mockup rooms-by-purpose.html, frame A "Center stream + snap rails",
// presets a Workstation + b Research). A standalone building block: the page
// owns every piece of room state and hands it over through the props below;
// this file owns the chrome, the rails, the "+" shelf, the phone bar and this
// device's layout. Nothing here fetches. GameStepPage mounts it behind
// utils/roomPurposeLayout's gate (shellPresetFor; the switch ON since 10-01),
// for learn/research rooms only.
//
// Desktop ≥1100: [left rail] [center: stage slot + the feed + the composer,
// max 760px] [right rail], rails a fixed ~300px (64px when empty: just "+").
// Narrow desktop 768-1099: both rails fold to icon strips (a panel opens in a
// sheet). Phones <768: the center only, the bar [Chat] [Tools +] [Files]
// [People], panels as bottom sheets.
//
// MOUNT CONTRACT (every prop optional unless marked):
//   gameId*        the room id (this device's layout is keyed on it)
//   purpose*       'learn' | 'research' (utils/roomPurposeLayout.presetFor)
//   preset         the preset object (default: presetFor(purpose))
//   name           the room's name
//   isMobile / isNarrow   the page's viewport answers (default: useViewport)
//   mode           'dark' | 'light' | 'auto' (default 'dark': rooms are dark today)
//   accent         a #hex that overrides the preset's accent
//   height         the frame's CSS height (default '100dvh')
//   onBack         ‹ in the top bar (absent: no back button)
//   battery        node: the PowerMeter chip (top bar, both layouts)
//   promote        node: the Promote door (top bar, desktop only; ≤108px wide;
//                  on phones put Promote in menuItems)
//   users          the page's activeUsers (the seats present): People + faces.
//                  Absent = no People panel anywhere.
//   hereCount      overrides "N here" (default users.length)
//   viewerSeatId   this browser's seat id ("(you)")
//   menuItems      [{ key, label, icon?, onSelect, on? }] for ⋯ (Export chat,
//                  Files, Room details…). icon: an emoji or an RsIcon name.
//   hideMusicButton  hide the app's ♫ (default: preset.music === 'menu'):
//                  emits LofiRadio's own MUSIC_FAB_HIDDEN while mounted and
//                  adds ⋯ → Music (MUSIC_OPEN). false keeps the ♫ lane clear.
//   onOpenFiles    () => void: opens the page's FilesFolder. Absent = no Files.
//   onOpenExport   () => void: opens the page's ExportChatDialog. Absent = no Export.
//   renderNotes    ({ isMobile, open, onClose, embedded, tone, accent }) => node:
//                  the page's <NotesPanel/>. Absent = no Notes. On a desktop
//                  rail (and a narrow desktop's sheet) it is called with
//                  isMobile:false + embedded:true, inside `.rs-embed` (which
//                  folds NotesPanel's own box + title under the rail header);
//                  on phones with isMobile:true, open, onClose (NotesPanel
//                  draws its own bottom sheet).
//   stage          node on the stage (above the feed); stageActive keeps the
//                  (empty) stage slot visible for a portaled host
//   children       the feed (center column, flex 1: give it its own scroller)
//   composer       node under the feed (the composer)
//   onSlot         (name, element|null) for 'stage' | 'feed' | 'composer':
//                  stable DOM hosts for the page's reparent chains (the
//                  WatchPartyTheater / LivestreamPanel idiom: append, never remount)
//   storage        a Storage for the layout (tests; default window.localStorage, guarded)
//   onLayoutChange (layout) => void after each change
//
// WHERE IT MOUNTS: never inside an element whose class holds a substring the
// room theme targets (card, panel, surface, message, container, wrapper…):
// stored themes ship `[class*='panel'] * { color: inherit }`, which ties the
// chrome's own color rules. The gate is shellPresetFor(status, { on, bot,
// gameType }) (utils/roomPurposeLayout): null = today's page, byte for byte.

const noop = () => {};

function toneOf(mode) {
  if (mode === 'light') return 'light';
  if (mode !== 'auto') return 'dark';
  try { return window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'; } catch (e) { return 'dark'; }
}

export default function RoomShellFrame({
  gameId, purpose, preset: presetProp = null, name = '',
  isMobile: isMobileProp, isNarrow: isNarrowProp, mode: modeProp = 'dark', accent: accentProp = null, height = '100dvh',
  onBack = null, battery = null, promote = null,
  users, hereCount = null, viewerSeatId = null, menuItems = [],
  hideMusicButton: hideMusicProp,
  onOpenFiles, onOpenExport, renderNotes,
  stage = null, stageActive = false, children = null, composer = null, onSlot = null,
  storage, onLayoutChange = null,
}) {
  const vp = useViewport();
  const isMobile = typeof isMobileProp === 'boolean' ? isMobileProp : vp.isMobile;
  const isNarrow = !isMobile && (typeof isNarrowProp === 'boolean' ? isNarrowProp : vp.isNarrowDesktop);
  const shape = isMobile ? 'phone' : (isNarrow ? 'narrow' : 'wide');

  const preset = useMemo(() => presetProp || presetFor(purpose), [presetProp, purpose]);
  const roomPurpose = (preset && preset.purpose) || purpose || null;
  const mode = cleanMode(modeProp);
  const accent = cleanAccent(accentProp || (preset && preset.accent));
  const hideMusic = typeof hideMusicProp === 'boolean' ? hideMusicProp : !!(preset && preset.music === 'menu');
  const tone = toneOf(mode);

  const available = useMemo(() => availableIds({
    files: typeof onOpenFiles === 'function',
    notes: typeof renderNotes === 'function',
    people: Array.isArray(users),
    export: typeof onOpenExport === 'function',
  }), [onOpenFiles, renderNotes, users, onOpenExport]);

  // ── this device's layout (owner default = the preset) ─────────────────────
  const storeKey = `${gameId || ''}|${roomPurpose || ''}`;
  const [layoutState, setLayoutState] = useState(() => ({ key: storeKey, layout: loadLayout(gameId, preset, storage) }));
  let layout = layoutState.layout;
  if (layoutState.key !== storeKey) {
    // another room (or purpose) under the same mount reads its own layout
    layout = loadLayout(gameId, preset, storage);
    setLayoutState({ key: storeKey, layout });
  }
  const setLayout = useCallback((fn) => {
    setLayoutState((s) => {
      const next = fn(s.layout);
      return next === s.layout ? s : { key: s.key, layout: next };
    });
  }, []);
  // a CHANGE (never a load) is kept on this device and reported
  const ctxRef = useRef(null);
  ctxRef.current = { gameId, preset, storage, onLayoutChange };
  const seenRef = useRef(null);
  useEffect(() => {
    const prev = seenRef.current;
    seenRef.current = layoutState;
    if (!prev || prev === layoutState || prev.key !== layoutState.key) return;
    const c = ctxRef.current;
    saveLayout(c.gameId, layoutState.layout, c.preset, c.storage);
    if (c.onLayoutChange) c.onLayoutChange(layoutState.layout);
  }, [layoutState]);

  const onAddAt = useCallback((id, side) => setLayout((l) => addPanel(l, id, side)), [setLayout]);
  const onToggle = useCallback((id) => setLayout((l) => toggleCollapsed(l, id)), [setLayout]);
  const onMove = useCallback((id) => setLayout((l) => movePanel(l, id)), [setLayout]);
  const onRemove = useCallback((id) => setLayout((l) => removePanel(l, id)), [setLayout]);
  const onReset = useCallback(() => setLayout(() => defaultLayout(preset)), [setLayout, preset]);

  // ── what is open: the "+" shelf, a sheet ('tools' | 'people' | a panel id) ─
  const [shelf, setShelf] = useState(null); // { side } | null
  const [sheet, setSheet] = useState(null);
  const closeSheet = useCallback(() => setSheet(null), []);

  // a door panel runs the page's handler; an inline one opens where it can
  const openPanel = useCallback((id) => {
    const p = panelFor(id);
    if (!p || !available.has(id)) return;
    if (p.render === 'files') { setSheet(null); onOpenFiles(); return; }
    if (p.render === 'export') { setSheet(null); onOpenExport(); return; }
    setSheet(id);
  }, [available, onOpenFiles, onOpenExport]);

  // ── the ♫ button: a quiet purpose moves it into ⋯ (LofiRadio's own events) ─
  useEffect(() => {
    if (!hideMusic) return undefined;
    emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: true });
    return () => emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: false });
  }, [hideMusic]);
  const items = useMemo(() => {
    const list = (Array.isArray(menuItems) ? menuItems : []).filter((it) => it && it.key && it.label);
    if (hideMusic && !list.some((it) => it.key === 'music')) {
      list.push({ key: 'music', label: 'Music', icon: 'music', onSelect: () => emitGameEvent(GAME_EVENTS.MUSIC_OPEN, {}) });
    }
    return list;
  }, [menuItems, hideMusic]);

  // ── slot hosts for the page's portals ─────────────────────────────────────
  const slotCbRef = useRef(onSlot);
  slotCbRef.current = onSlot;
  const stageRef = useCallback((el) => { if (slotCbRef.current) slotCbRef.current('stage', el); }, []);
  const feedRef = useCallback((el) => { if (slotCbRef.current) slotCbRef.current('feed', el); }, []);
  const composerRef = useCallback((el) => { if (slotCbRef.current) slotCbRef.current('composer', el); }, []);

  // ── panel bodies ──────────────────────────────────────────────────────────
  const notesNode = (extra) => (renderNotes ? renderNotes({ tone, accent, ...extra }) : null);
  const renderBody = (panel) => {
    if (panel.render === 'people') return <RoomPeople users={users} viewerSeatId={viewerSeatId} />;
    if (panel.render === 'notes') return <div className="rs-embed">{notesNode({ isMobile: false, open: true, onClose: noop, embedded: true })}</div>;
    const go = panel.render === 'files' ? onOpenFiles : onOpenExport;
    return (
      <>
        <div className="rs-blurb">{panel.blurb}</div>
        <button type="button" className="rs-door" data-prop-ui="" data-rs-door={panel.id} onClick={() => go && go()}>
          <span className="rs-doorico" aria-hidden="true"><RsIcon name={panel.icon} size={16} /></span>
          <span className="rs-doortx">{panel.door}</span>
          <span className="rs-doorgo" aria-hidden="true"><RsIcon name="chevR" size={15} /></span>
        </button>
      </>
    );
  };
  const onRail = (side) => (layout[side] || []).filter((e) => available.has(e.id));
  const leftEntries = onRail('left');
  const rightEntries = onRail('right');
  const placedAvail = [...leftEntries, ...rightEntries].map((e) => panelFor(e.id)).filter(Boolean);
  const shelfTools = unplacedPanels(layout, roomPurpose, available);
  const railW = (entries) => (shape === 'wide' && entries.length ? 'clamp(248px, 21vw, 300px)' : '64px');
  const cols = shape === 'phone' ? 'minmax(0, 1fr)' : `${railW(leftEntries)} minmax(0, 1fr) ${railW(rightEntries)}`;
  const shared = { mode, accent, isMobile };
  const count = Number.isFinite(hereCount) ? hereCount : (Array.isArray(users) ? users.length : 0);

  const railProps = (side, entries) => ({
    side, entries, renderBody, onAdd: (s) => setShelf({ side: s }), onToggle, onMove, onRemove,
    mini: shape === 'narrow', onOpenPanel: openPanel, openId: sheet,
  });

  // the one sheet that is up (tools · people · an inline panel)
  let sheetNode = null;
  if (sheet === 'tools') {
    sheetNode = (
      <RoomShellSheet open onClose={closeSheet} title="Tools" sub="Tap one to open it." kind="tools" testId="rs-tools" {...shared}>
        <div className="rs-tgrid">
          {placedAvail.map((p) => (
            <button key={p.id} type="button" className="rs-tt" data-prop-ui="" data-rs-tile={p.id} onClick={() => openPanel(p.id)}>
              <span className="rs-tic" aria-hidden="true"><RsIcon name={p.icon} size={18} /></span>{p.label}
            </button>
          ))}
          <button type="button" className="rs-tt" data-add="1" data-prop-ui="" data-rs-tile="add" onClick={() => { setSheet(null); setShelf({ side: null }); }}>
            <span className="rs-tic" aria-hidden="true"><RsIcon name="plus" size={18} /></span>Add a tool
          </button>
        </div>
      </RoomShellSheet>
    );
  } else if (sheet === 'people' && available.has('people')) {
    sheetNode = (
      <RoomShellSheet open onClose={closeSheet} title="People" sub={`${count} here`} kind="people" testId="rs-people" {...shared}>
        <RoomPeople users={users} viewerSeatId={viewerSeatId} />
      </RoomShellSheet>
    );
  } else if (sheet === 'notes' && available.has('notes')) {
    sheetNode = isMobile
      ? notesNode({ isMobile: true, open: true, onClose: closeSheet, embedded: false })
      : (
        <RoomShellSheet open onClose={closeSheet} title="Notes" kind="notes" testId="rs-notes" {...shared}>
          <div className="rs-embed">{notesNode({ isMobile: false, open: true, onClose: closeSheet, embedded: true })}</div>
        </RoomShellSheet>
      );
  }

  return (
    <div
      className="rs"
      data-room-shell="1"
      data-rs-purpose={roomPurpose || ''}
      data-rs-mode={mode}
      data-layout={shape}
      style={{ ...accentVars(accent), height }}
    >
      <RoomShellStyle />
      <RoomShellTopBar
        name={name}
        purposeLabel={(preset && preset.name) || ''}
        onBack={onBack}
        battery={battery}
        promote={promote}
        users={Array.isArray(users) ? users : null}
        hereCount={hereCount}
        viewerSeatId={viewerSeatId}
        onPeople={available.has('people') ? () => setSheet('people') : null}
        menuItems={items}
        fab={!hideMusic}
        {...shared}
      />
      <div className="rs-body" style={{ gridTemplateColumns: cols, gridTemplateRows: 'minmax(0, 1fr)' }}>
        {shape === 'phone' ? null : <RoomRail {...railProps('left', leftEntries)} />}
        {/* a div with role="main", never <main>: 13 of 108 stored room themes style `main` (an
            infinite animation, an inset shadow, a ::before glow over the stage) */}
        <div className="rs-center" role="main" data-rs-center="">
          <div className="rs-col">
            <div className="rs-stg" data-rs-slot="stage" ref={stageRef} hidden={!(stage || stageActive)}>{stage}</div>
            <div className="rs-feed" data-rs-slot="feed" ref={feedRef}>{children}</div>
            <div className="rs-cmp" data-rs-slot="composer" ref={composerRef}>{composer}</div>
          </div>
        </div>
        {shape === 'phone' ? null : <RoomRail {...railProps('right', rightEntries)} />}
      </div>
      {shape === 'phone' ? (
        <RoomShellPhoneBar
          active={sheet === 'people' ? 'people' : (sheet ? 'tools' : 'chat')}
          available={available}
          onSelect={(id) => {
            if (id === 'chat') { setSheet(null); return; }
            if (id === 'tools') { setSheet('tools'); return; }
            openPanel(id);
          }}
        />
      ) : null}
      {sheetNode}
      <ToolShelf
        open={!!shelf}
        onClose={() => setShelf(null)}
        tools={shelfTools}
        side={shelf ? shelf.side : null}
        onAdd={(id) => {
          onAddAt(id, shelf && shelf.side);
          setShelf(null);
          if (isMobile) setSheet('tools');
        }}
        onReset={sameLayout(layout, defaultLayout(preset)) ? null : onReset}
        {...shared}
      />
    </div>
  );
}
